import React, { useState, useEffect } from 'react';
import { createCampaign, updateCampaign, uploadCampaignBanner } from '../services/apiService';
import config from '../config';

const slugify = (s) =>
  String(s || '').toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const imageBase = (config.baseURL || '').replace(/\/api\/?$/, '');

const toLocalInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const tz = dt.getTimezoneOffset();
  return new Date(dt.getTime() - tz * 60 * 1000).toISOString().slice(0, 16);
};

const CampaignFormModal = ({ campaign, onClose, onSaved }) => {
  const isEdit = !!campaign;
  const [form, setForm] = useState({
    name: campaign?.name || '',
    slug: campaign?.slug || '',
    description: campaign?.description || '',
    startsAt: toLocalInput(campaign?.startsAt),
    endsAt: toLocalInput(campaign?.endsAt),
    accentColor: campaign?.accentColor || '#E1444B',
    bannerImage: campaign?.bannerImage || '',
    displayOrder: campaign?.displayOrder ?? 100,
  });
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slugTouched) setForm((f) => ({ ...f, slug: slugify(f.name) }));
  }, [form.name, slugTouched]);

  const onBannerFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const r = await uploadCampaignBanner(file, form.slug || slugify(form.name));
      setForm((f) => ({ ...f, bannerImage: r?.data?.path || '' }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
        bannerImage: form.bannerImage || null,
        accentColor: form.accentColor || null,
        displayOrder: Number(form.displayOrder) || 100,
      };
      if (isEdit) await updateCampaign(campaign._id, body);
      else await createCampaign(body);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="px-6 py-4 space-y-3 text-sm">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          )}

          <label className="block">
            <span className="text-gray-700">Name</span>
            <input
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoFocus
            />
          </label>

          <label className="block">
            <span className="text-gray-700">Slug</span>
            <input
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }}
              required
            />
            <span className="block mt-1 text-[10px] text-gray-400">URL: /products?campaign={form.slug || '...'}</span>
          </label>

          <label className="block">
            <span className="text-gray-700">Description (optional)</span>
            <textarea
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-gray-700">Starts at</span>
              <input
                type="datetime-local"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-gray-700">Ends at</span>
              <input
                type="datetime-local"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={form.endsAt}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-gray-700">Accent color</span>
              <input
                type="color"
                className="mt-1 w-full h-10 px-1 border border-gray-300 rounded-lg"
                value={form.accentColor}
                onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-gray-700">Display order</span>
              <input
                type="number"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
              />
            </label>
          </div>

          <label className="block">
            <span className="text-gray-700">Banner image (optional, ≤2MB)</span>
            <input
              type="file"
              accept="image/*"
              onChange={onBannerFile}
              disabled={uploading}
              className="mt-1 block w-full text-sm"
            />
            {uploading && <div className="mt-1 text-xs text-gray-500">Uploading...</div>}
            {form.bannerImage && (
              <div className="mt-2">
                <img
                  src={`${imageBase}${form.bannerImage}`}
                  alt="banner"
                  className="h-20 rounded border border-gray-200"
                />
                <div className="mt-1 text-[10px] text-gray-400 break-all">{form.bannerImage}</div>
              </div>
            )}
          </label>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >Cancel</button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-[#E1444B] text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create campaign')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignFormModal;
